import React, { useEffect, useState, useId } from "react";
import { Link } from "react-router-dom";
import { browsePair } from "../lib/api";
import "./common-questions.css";

export const VERDICT_STYLE = {
  AVOID: { label: "Avoid", bg: "#fdecea", fg: "#b42318", dot: "#d92d20", band: "#fbe3df" },
  CAUTION: { label: "Caution", bg: "#fef6e7", fg: "#b45309", dot: "#f79009", band: "#fbeed2" },
  USE_WITH_CAUTION: { label: "Caution", bg: "#fef6e7", fg: "#b45309", dot: "#f79009", band: "#fbeed2" },
  SAFE: { label: "No label conflict found", bg: "#e9f6ee", fg: "#1f7a3f", dot: "#17b26a", band: "#dcf1e4" },
  SAFE_TOGETHER: { label: "No label conflict found", bg: "#e9f6ee", fg: "#1f7a3f", dot: "#17b26a", band: "#dcf1e4" },
  INSUFFICIENT_DATA: { label: "Not yet reviewed", bg: "#f5ecde", fg: "#7a7468", dot: "#b8ab93", band: "#efe4d2" },
};

export function VerdictBadge({ verdict, testid }) {
  const s = VERDICT_STYLE[verdict] || VERDICT_STYLE.INSUFFICIENT_DATA;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
      style={{ background: s.bg, color: s.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

// Existing verdict exports above are preserved for other consumers.
export default function CommonQuestions({ questions }) {
  const [expanded, setExpanded] = useState(false);
  const [statuses, setStatuses] = useState({});
  const gridId = useId();
  const signature = JSON.stringify((questions || []).map((q) => [q.medication_id, q.ingredient_id]));

  useEffect(() => {
    let disposed = false, busy = false;
    const pairs = JSON.parse(signature);
    const key = (m, i) => JSON.stringify([m, i]);
    async function refresh() {
      if (busy || document.hidden) return;
      busy = true;
      // Drop prior badge before refresh; never retain an approval on a failed request.
      setStatuses({});
      await Promise.all(pairs.map(async ([m, i]) => {
        if (!m || !i) return;
        try {
          const result = await browsePair(encodeURIComponent(m), encodeURIComponent(i));
          // browse_pair returns an interaction ONLY after is_public_verdict_row.
          const state = result?.has_interaction === true && !!result?.interaction ? "reviewed" : "pending";
          if (!disposed) setStatuses((prev) => ({ ...prev, [key(m, i)]: state }));
        } catch {
          if (!disposed) setStatuses((prev) => ({ ...prev, [key(m, i)]: "error" }));
        }
      }));
      busy = false;
    }
    refresh();
    const timer = setInterval(refresh, 60000);
    const focus = () => refresh();
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    return () => {
      disposed = true;
      clearInterval(timer);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, [signature]);

  return (
    <section className="bs-questions" data-testid="common-questions">
      <p className="bq-eyebrow">Browse by combination</p>
      <h2>Your skincare. Your medication. Your questions.</h2>
      <p className="bq-intro">Explore a combination to see its current review status and available findings.</p>
      <p className="bq-note">
        ⓘ <span>“Not yet reviewed” means a pharmacist-approved finding is not available yet. “Pharmacist reviewed” describes the review, not whether the combination is safe.</span>
      </p>
      {!questions ? (
        <p role="status">Loading combinations…</p>
      ) : questions.length === 0 ? (
        <p>No combinations are available to browse yet.</p>
      ) : (
        <>
          <div id={gridId} className={"bq-grid" + (expanded ? " bq-expanded" : "")}>
            {questions.map((q, i) => {
              const state = statuses[JSON.stringify([q.medication_id, q.ingredient_id])];
              const label =
                state === "reviewed" ? "Pharmacist reviewed"
                  : state === "pending" ? "Not yet reviewed"
                    : state === "error" ? "Review status unavailable"
                      : "Checking review status…";
              return (
                <article
                  className={"bq-card" + (i >= 4 ? " bq-extra" : "")}
                  key={JSON.stringify([q.medication_id, q.ingredient_id, i])}
                  data-testid={`question-card-${i}`}
                >
                  <span className="bq-status" data-testid={`question-status-${i}`}>{label}</span>
                  <h3>{q.question}</h3>
                  {q.medication_id && q.ingredient_id ? (
                    <Link
                      to={`/browse/pair/${encodeURIComponent(q.medication_id)}/${encodeURIComponent(q.ingredient_id)}`}
                      data-testid={`question-explore-${i}`}
                    >
                      Explore combination <span aria-hidden="true">→</span>
                    </Link>
                  ) : (
                    <span className="bq-status">Combination link unavailable</span>
                  )}
                </article>
              );
            })}
          </div>
          {questions.length > 4 && (
            <button
              className="bq-more"
              type="button"
              aria-expanded={expanded}
              aria-controls={gridId}
              onClick={() => setExpanded(!expanded)}
              data-testid="questions-toggle"
            >
              {expanded ? "Show fewer questions" : "Show all questions"} <span aria-hidden="true">{expanded ? "↑" : "↓"}</span>
            </button>
          )}
        </>
      )}
      <Link className="bq-browse" to="/browse" data-testid="questions-browse-all">
        Browse all combinations <span aria-hidden="true">→</span>
      </Link>
    </section>
  );
}
